import Debug from 'debug'
import { Code, ConnectError } from '@connectrpc/connect'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { AppError } from '@jotta/types/AppError'
import { GrpcApiError } from '@jotta/types/GrpcApiError'

const debug = Debug('jotta:query')

const isTest = process.env.NODE_ENV === 'test'
const isDevelopment = process.env.NODE_ENV === 'development'

const queryCache = new QueryCache({
  onError: error => {
    debug(error)
  },
})

const mutationCache = new MutationCache({
  onError: error => {
    debug(error)
  },
})

/**
 * The request got no answer: the network is down or coming back (a laptop
 * waking from sleep, Wi-Fi or VPN reconnecting), or the server is briefly
 * unavailable. Such failures are worth retrying for longer than others.
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof ConnectError) {
    if (
      error.code === Code.Unavailable ||
      error.code === Code.DeadlineExceeded
    ) {
      return true
    }
    // A fetch that failed arrives as Unknown with the browser's message. Any
    // other TypeError cause is a bug in our code, not a network failure.
    return error.code === Code.Unknown && fetchFailure.test(error.rawMessage)
  }
  return error instanceof TypeError && fetchFailure.test(error.message)
}

// Chrome, Firefox and Safari wordings of a fetch that got no response, and
// Chrome's "network error" and Firefox's "Error in input stream" when the
// connection drops while the transport reads the response body.
const fetchFailure =
  /failed to fetch|network ?error|load failed|network connection was lost|error in input stream/i

/**
 * Network failures: up to 5 retries. They fail fast, so with the default
 * backoff (1, 2, 4, 8, 16 s) the query gives up after about 30 s.
 */
const networkRetries = 5

/**
 * Timeouts keep the normal 3 retries: each attempt already waits for the
 * 10 s transport timeout, so 4 attempts plus 7 s of backoff is about 47 s.
 * The network budget would hold the page for about 90 s.
 */
const defaultRetries = 3

function retryBudget(error: unknown): number {
  if (error instanceof ConnectError && error.code === Code.DeadlineExceeded) {
    return defaultRetries
  }
  return isNetworkError(error) ? networkRetries : defaultRetries
}

/**
 * Rejected as unauthenticated, by Connect or by the legacy grpc-web client
 * (an AppError or GrpcApiError, possibly wrapping the original error).
 */
function isUnauthenticated(error: unknown): boolean {
  if (error instanceof ConnectError) {
    return error.code === Code.Unauthenticated
  }
  if (error instanceof AppError) {
    return error.isUnauthenticated
  }
  return error instanceof GrpcApiError && error.isNotAuthenticated
}

/**
 * NOT_FOUND is not transient, so retrying cannot succeed. Retrying it caused
 * request storms when views held refs to photos deleted on other devices —
 * every missing photo multiplied into 4 requests, and batched fetches
 * multiplied whole batches.
 * Authentication recovery is bounded by the transport (Connect) and the grpc
 * middleware (legacy grpc-web); query retries must not restart that recovery
 * after it has failed.
 */
export function retryQueryOnError(failureCount: number, error: unknown) {
  if (error instanceof ConnectError && error.code === Code.NotFound) {
    return false
  }
  if (isUnauthenticated(error)) {
    return false
  }
  return failureCount < retryBudget(error)
}

/**
 * Development: only network failures are retried, so server errors still
 * show at once while debugging, but a laptop waking from sleep does not end
 * on the error page.
 */
export function retryNetworkErrorsOnly(failureCount: number, error: unknown) {
  return isNetworkError(error) && failureCount < retryBudget(error)
}

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      retry: isTest
        ? false
        : isDevelopment
          ? retryNetworkErrorsOnly
          : retryQueryOnError,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      throwOnError: true,
    },
    mutations: {
      throwOnError: true,
    },
  },
})
